<script setup lang="ts">
import RcPartStatusBreakdown from '@/components/rc/common/RcPartStatusBreakdown.vue'
import { IndividualPart, PartType, partTypes } from '@/types/rc/miners/PartType'
import { computed } from 'vue'
import { type IConsolidationPartsDict } from './MinersConsolidationGridColumn.vue'
import { Icon } from '@iconify/vue'
//@ts-expect-error needed
import { Container, Draggable } from 'vue-dndrop'

const part = computed(() => props.minerParts?.[props.partName])
const type = computed(() => partTypes[props.partName])
const active = computed(() => props.activeDragType === type.value)

// Parts were expected to hover:bg-fill-tertiary only when they are matching the active part type
// I tried by all means setting a dynamic class binding con `container` but that just breaks them
// const hoverBgClass = computed(() =>
//   !props.activeDragType || !!active.value ? 'hover:bg-fill-tertiary' : 'hover:bg-fill-secondary'
// )

// Can it be dragged/swapped/moved/occupied?
const apt = computed(
  () =>
    (type.value === PartType.Fan && part.value?.model) || // Fan with model
    (part.value?.model && part.value?.sn && !part.value?.moved_to_inventory) || // Part with model, sn and not moved to inventory
    !part.value?.id // Empty slot (draggable won't be rendered)
)

const props = defineProps<{
  partName: IndividualPart
  minerParts: IConsolidationPartsDict
  activeDragType: PartType | undefined
}>()

// Runs for *every container upon drag start* to check if the drop is allowed
// partName is the IndividualPart of *the container*, draggingType is the PartType of the part *being dragged*
const shouldAcceptDrop = (partName: IndividualPart, draggingType: PartType) => draggingType === type.value && apt.value // matches type and the part is apt
</script>

<template>
  <!-- A draggable container with a single element -->
  <container
    behaviour="drop-zone"
    drag-handle-selector=".handle"
    :should-accept-drop="(srcOptions: any, payload: any) => shouldAcceptDrop(partName, payload.type)"
    :get-child-payload="() => minerParts && part"
    class="flex items-center gap-2 font-extralight rounded hover:bg-fill-tertiary"
  >
    <!-- I really tried handling hover state with class bindings but that's broken for the container component -->
    <draggable v-if="part && part.id" class="w-full" :drag-not-allowed="!apt">
      <div class="flex items-center gap-2">
        <div :style="`opacity: ${apt ? '1' : '0.3'};`">
          <icon icon="ph:dots-six-vertical-bold" class="handle" />
        </div>
        <span
          class="flex items-center justify-between gap-2 w-full border border-component-stroke-primary border-l-4 rounded p-2 py-1 text-xs"
          :style="`border-left-color: ${apt ? part?.color : 'var(--component-stroke-primary)'}`"
        >
          {{ part?.sn ?? 'N/A' }} {{ part?.model ? `(${part?.model})` : '' }}
          <rc-part-status-breakdown :part="part" :disabled="active" />
        </span>
      </div>
    </draggable>
    <div v-if="!part?.id" class="flex items-center gap-2 w-full rounded p-2 py-1">
      <icon icon="ph:dots-six-vertical-bold" class="handle opacity-0" />
      <p class="w-full p-2 rounded text-xs text-tertiary bg-fill-table-stripe hover:bg-fill-secondary">Missing part</p>
    </div>
  </container>
</template>

<style scoped>
.handle {
  cursor: grab;
}

body.dndrop-no-user-select .handle {
  cursor: grabbing;
}
</style>
